import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { receitas as receitasApi, descontos as descontosApi, meses as mesesApi } from '../lib/api';
import { useMonth } from '../context/MonthContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, parseCurrency, DEDUCTION_TYPES } from '../utils/format';
import { canAddReceita, getPlanLimits } from '../utils/planLimits';
import { UpgradePrompt, LimitBadge } from '../components/UpgradePrompt';
import { CategorySelector } from '../components/CategorySelector';
import LoadingTimeout from '../components/LoadingTimeout';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useRegisterRefresh } from '../context/RefreshContext';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Wallet,
  TrendingDown,
  TrendingUp,
  DollarSign,
  MoreHorizontal,
  Repeat,
  Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';

const deductionIcons = {
  irrf: DollarSign,
  inss: DollarSign,
  plano_saude: DollarSign,
  plano_saude_custeio: DollarSign,
  sindicato: DollarSign,
  emprestimo_consignado: DollarSign,
  outros: MoreHorizontal,
};

function ReceitasPage() {
  const { mesData, refreshData, currentMonth } = useMonth();
  const { user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const limits = getPlanLimits(plan);
  const [receitas, setReceitas] = useState([]);
  const [descontos, setDescontos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  
  // Modal states
  const [showReceitaModal, setShowReceitaModal] = useState(false);
  const [showDescontoModal, setShowDescontoModal] = useState(false);
  const [editingReceita, setEditingReceita] = useState(null);
  const [editingDesconto, setEditingDesconto] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form states
  const [receitaForm, setReceitaForm] = useState({
    descricao: '',
    tipo: '',
    valor_bruto: '',
    is_fixo: true,
  });
  
  const [descontoForm, setDescontoForm] = useState({
    descricao: '',
    tipo: '',
    valor: '',
    is_fixo: true,
  });

  // Load data
  const loadData = useCallback(async () => {
    if (!mesData?.id) return;
    
    setLoading(true);
    try {
      const [receitasRes, descontosRes] = await Promise.all([
        receitasApi.list(mesData.id),
        descontosApi.list(mesData.id),
      ]);
      setReceitas(receitasRes);
      setDescontos(descontosRes);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [mesData?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { timedOut, resetTimeout } = useLoadingTimeout(loading, loadData);
  useRegisterRefresh(loadData);

  // Calculate totals
  const totalBruto = receitas.reduce((sum, r) => sum + r.valor_bruto, 0);
  const totalDescontos = descontos.reduce((sum, d) => sum + d.valor, 0);
  const totalLiquido = totalBruto - totalDescontos;

  // Split fixed vs sporadic
  const receitasFixas = receitas.filter((r) => r.is_fixo);
  const receitasEsporadicas = receitas.filter((r) => !r.is_fixo);

  // Handle receita
  const handleSaveReceita = async () => {
    if (!mesData?.id) return;

    // Enforce tier limit for new receitas
    if (!editingReceita && !canAddReceita(plan, receitas.length)) {
      return;
    }

    const data = {
      mes_id: mesData.id,
      descricao: receitaForm.descricao || receitaForm.tipo,
      tipo: receitaForm.tipo,
      valor_bruto: parseCurrency(receitaForm.valor_bruto),
      is_fixo: receitaForm.is_fixo,
    };

    try {
      if (editingReceita) {
        await receitasApi.update(editingReceita.id, data);
      } else {
        await receitasApi.create(data);
      }
      setShowReceitaModal(false);
      setEditingReceita(null);
      setReceitaForm({ descricao: '', tipo: '', valor_bruto: '', is_fixo: true });
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to save receita:', error);
    }
  };

  const handleEditReceita = (receita) => {
    setEditingReceita(receita);
    setReceitaForm({
      descricao: receita.descricao,
      tipo: receita.tipo,
      valor_bruto: receita.valor_bruto.toString().replace('.', ','),
      is_fixo: receita.is_fixo ?? false,
    });
    setShowReceitaModal(true);
  };

  const handleCopyEsporadicas = async () => {
    setCopying(true);
    try {
      await mesesApi.copiarReceitasEsporadicasAnterior(currentMonth.mes, currentMonth.ano);
      await loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to copy sporadic receitas:', error);
    } finally {
      setCopying(false);
    }
  };

  const handleDeleteReceita = async (id) => {
    try {
      await receitasApi.delete(id);
      setDeleteConfirm(null);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to delete receita:', error);
    }
  };

  // Handle desconto
  const handleSaveDesconto = async () => {
    if (!mesData?.id) return;
    
    const data = {
      mes_id: mesData.id,
      descricao: descontoForm.descricao || DEDUCTION_TYPES[descontoForm.tipo] || descontoForm.tipo,
      tipo: descontoForm.tipo,
      valor: parseCurrency(descontoForm.valor),
      is_fixo: descontoForm.is_fixo,
    };

    try {
      if (editingDesconto) {
        await descontosApi.update(editingDesconto.id, data);
      } else {
        await descontosApi.create(data);
      }
      setShowDescontoModal(false);
      setEditingDesconto(null);
      setDescontoForm({ descricao: '', tipo: '', valor: '', is_fixo: true });
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to save desconto:', error);
    }
  };

  const handleEditDesconto = (desconto) => {
    setEditingDesconto(desconto);
    setDescontoForm({
      descricao: desconto.descricao,
      tipo: desconto.tipo,
      valor: desconto.valor.toString().replace('.', ','),
      is_fixo: desconto.is_fixo ?? false,
    });
    setShowDescontoModal(true);
  };

  const handleDeleteDesconto = async (id) => {
    try {
      await descontosApi.delete(id);
      setDeleteConfirm(null);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Failed to delete desconto:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        {timedOut ? (
          <LoadingTimeout
            timedOut={timedOut}
            onRetry={() => {
              resetTimeout();
              loadData();
            }}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1B2A4A] border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-slate-600">Carregando receitas...</p>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="receitas-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-2 border-slate-200 shadow-lg" data-testid="total-bruto-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#1B2A4A] rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <span className="text-base font-semibold text-slate-600">Total Bruto</span>
              </div>
              <p className="text-2xl font-extrabold text-[#1B2A4A] tabular-nums">
                {formatCurrency(totalBruto)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-red-200 bg-red-50 shadow-lg" data-testid="total-descontos-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-white" />
                </div>
                <span className="text-base font-semibold text-red-700">Total Descontos</span>
              </div>
              <p className="text-2xl font-extrabold text-red-700 tabular-nums">
                - {formatCurrency(totalDescontos)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-300 bg-green-50 shadow-lg" data-testid="total-liquido-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-base font-semibold text-green-700">Total Líquido</span>
              </div>
              <p className="text-2xl font-extrabold text-green-700 tabular-nums">
                {formatCurrency(totalLiquido)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Receitas Fixas — apply to all months */}
        <Card className="border-2 border-blue-300 bg-blue-50/40 shadow-lg" data-testid="receitas-fixas-section">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl font-bold text-[#1B2A4A] flex items-center gap-3">
                <Repeat size={24} className="text-blue-700" />
                Receitas Fixas
                {limits.receitasPerMonth !== Infinity && (
                  <LimitBadge current={receitas.length} max={limits.receitasPerMonth} />
                )}
              </CardTitle>
              <span className="text-sm text-slate-500">Aparecem em todos os meses</span>
            </div>
            <Button
              onClick={() => {
                setEditingReceita(null);
                setReceitaForm({ descricao: '', tipo: '', valor_bruto: '', is_fixo: true });
                setShowReceitaModal(true);
              }}
              disabled={!canAddReceita(plan, receitas.length)}
              className="h-12 w-full sm:w-auto px-4 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl disabled:opacity-50"
              data-testid="add-receita-fixa-btn"
            >
              <Plus size={20} className="mr-2" />
              Adicionar
            </Button>
          </CardHeader>
          <CardContent>
            {receitasFixas.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Repeat size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-lg">Nenhuma receita fixa</p>
                <p className="text-base">Adicione salário, aposentadoria, aluguel, etc.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {receitasFixas.map((receita) => (
                  <div
                    key={receita.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-500 transition-colors"
                    data-testid={`receita-item-${receita.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-12 h-12 shrink-0 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-lg font-semibold text-[#1B2A4A] break-words">{receita.descricao}</p>
                        <p className="text-base text-slate-500">{receita.tipo}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                      <span className="text-xl font-bold text-[#1B2A4A] tabular-nums">
                        {formatCurrency(receita.valor_bruto)}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditReceita(receita)}
                          className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#1B2A4A] hover:bg-slate-200 rounded-lg transition-colors"
                          aria-label="Editar"
                        >
                          <Edit2 size={20} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'receita', id: receita.id })}
                          className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                          aria-label="Excluir"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Receitas Esporádicas — only this month */}
        <Card className="border-2 border-slate-200 shadow-lg" data-testid="receitas-esporadicas-section">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl font-bold text-[#1B2A4A] flex items-center gap-3">
                <Wallet size={24} />
                Receitas Esporádicas
              </CardTitle>
              <span className="text-sm text-slate-500">Só este mês</span>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={handleCopyEsporadicas}
                disabled={copying}
                variant="outline"
                className="h-12 flex-1 sm:flex-initial px-3 sm:px-4 border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white rounded-xl"
                data-testid="copy-receitas-esporadicas-btn"
              >
                <Copy size={18} className="mr-2" />
                <span className="hidden sm:inline">{copying ? 'Copiando...' : 'Copiar do mês anterior'}</span>
                <span className="sm:hidden">{copying ? 'Copiando...' : 'Copiar'}</span>
              </Button>
              <Button
                onClick={() => {
                  setEditingReceita(null);
                  setReceitaForm({ descricao: '', tipo: '', valor_bruto: '', is_fixo: false });
                  setShowReceitaModal(true);
                }}
                disabled={!canAddReceita(plan, receitas.length)}
                className="h-12 flex-1 sm:flex-initial px-4 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl disabled:opacity-50"
                data-testid="add-receita-esporadica-btn"
              >
                <Plus size={20} className="mr-2" />
                Adicionar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {receitasEsporadicas.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Wallet size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-lg">Nenhuma receita esporádica neste mês</p>
                <p className="text-base">Ex: 13º salário, bônus, venda, presente.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {receitasEsporadicas.map((receita) => (
                  <div
                    key={receita.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 border-2 border-slate-200 rounded-xl hover:border-[#1B2A4A] transition-colors"
                    data-testid={`receita-item-${receita.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-12 h-12 shrink-0 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-lg font-semibold text-[#1B2A4A] break-words">{receita.descricao}</p>
                        <p className="text-base text-slate-500">{receita.tipo}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                      <span className="text-xl font-bold text-[#1B2A4A] tabular-nums">
                        {formatCurrency(receita.valor_bruto)}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditReceita(receita)}
                          className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#1B2A4A] hover:bg-slate-200 rounded-lg transition-colors"
                          aria-label="Editar"
                        >
                          <Edit2 size={20} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'receita', id: receita.id })}
                          className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                          aria-label="Excluir"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {!canAddReceita(plan, receitas.length) && (
          <UpgradePrompt feature="adicionar mais fontes de renda" />
        )}

        {/* Descontos Section */}
        <Card className="border-2 border-red-200 shadow-lg" data-testid="descontos-section">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-xl font-bold text-red-700 flex items-center gap-3">
              <TrendingDown size={24} />
              Descontos
            </CardTitle>
            <Button
              onClick={() => {
                setEditingDesconto(null);
                setDescontoForm({ descricao: '', tipo: '', valor: '', is_fixo: true });
                setShowDescontoModal(true);
              }}
              className="h-12 w-full sm:w-auto px-4 bg-red-600 hover:bg-red-700 rounded-xl"
              data-testid="add-desconto-btn"
            >
              <Plus size={20} className="mr-2" />
              Adicionar
            </Button>
          </CardHeader>
          <CardContent>
            {descontos.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <TrendingDown size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-lg">Nenhum desconto cadastrado</p>
                <p className="text-base">Adicione IRRF, INSS, plano de saúde, etc.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {descontos.map((desconto) => {
                  const Icon = deductionIcons[desconto.tipo] || DollarSign;
                  return (
                    <div
                      key={desconto.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:border-red-400 transition-colors"
                      data-testid={`desconto-item-${desconto.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-12 h-12 shrink-0 bg-red-500 rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-lg font-semibold text-red-800 break-words">{desconto.descricao}</p>
                            {desconto.is_fixo && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                                <Repeat size={12} />
                                Fixo
                              </span>
                            )}
                          </div>
                          <p className="text-base text-red-600">{DEDUCTION_TYPES[desconto.tipo]}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                        <span className="text-xl font-bold text-red-700 tabular-nums">
                          - {formatCurrency(desconto.valor)}
                        </span>
                        <div className="flex gap-1">
                        <button
                          onClick={() => handleEditDesconto(desconto)}
                          className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                          aria-label="Editar"
                        >
                          <Edit2 size={20} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'desconto', id: desconto.id })}
                          className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-200 rounded-lg transition-colors"
                          aria-label="Excluir"
                        >
                          <Trash2 size={20} />
                        </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Receita Modal */}
        <Dialog open={showReceitaModal} onOpenChange={setShowReceitaModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="receita-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1B2A4A]">
                {editingReceita ? 'Editar Receita' : 'Nova Receita'}
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                Preencha os dados da receita
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Categoria</Label>
                <CategorySelector
                  tipo="receita"
                  value={receitaForm.tipo}
                  onChange={(value) => setReceitaForm(prev => ({ ...prev, tipo: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Descrição</Label>
                <Input
                  value={receitaForm.descricao}
                  onChange={(e) => setReceitaForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder={receitaForm.tipo || 'Descrição da receita'}
                  className="h-14 text-lg border-2"
                  data-testid="receita-descricao-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Valor (R$)</Label>
                <Input
                  value={receitaForm.valor_bruto}
                  onChange={(e) => setReceitaForm(prev => ({ ...prev, valor_bruto: e.target.value }))}
                  placeholder="0,00"
                  className="h-14 text-lg border-2"
                  data-testid="receita-valor-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Tipo de receita</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setReceitaForm(prev => ({ ...prev, is_fixo: true }))}
                    className={`h-14 rounded-xl border-2 text-base font-semibold flex items-center justify-center gap-2 transition-all ${
                      receitaForm.is_fixo
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400'
                    }`}
                    data-testid="receita-fixa-btn"
                  >
                    <Repeat size={18} />
                    <span className="hidden sm:inline">Fixa (todo mês)</span>
                    <span className="sm:hidden">Fixa</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReceitaForm(prev => ({ ...prev, is_fixo: false }))}
                    className={`h-14 rounded-xl border-2 text-base font-semibold flex items-center justify-center gap-2 transition-all ${
                      !receitaForm.is_fixo
                        ? 'border-slate-500 bg-slate-100 text-slate-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400'
                    }`}
                    data-testid="receita-esporadica-btn"
                  >
                    1x Esporádica
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReceitaModal(false)}
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                <X size={18} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleSaveReceita}
                className="h-12 w-full sm:w-auto px-6 text-base bg-[#1B2A4A] hover:bg-[#2C3E50]"
                data-testid="save-receita-btn"
              >
                <Save size={18} className="mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Desconto Modal */}
        <Dialog open={showDescontoModal} onOpenChange={setShowDescontoModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="desconto-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-700">
                {editingDesconto ? 'Editar Desconto' : 'Novo Desconto'}
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                Preencha os dados do desconto
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Tipo</Label>
                <Select
                  value={descontoForm.tipo}
                  onValueChange={(value) => setDescontoForm(prev => ({ ...prev, tipo: value }))}
                >
                  <SelectTrigger className="h-14 text-lg border-2" data-testid="desconto-tipo-select">
                    <SelectValue placeholder="Selecione o tipo de desconto" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DEDUCTION_TYPES).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-lg py-3">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Descrição</Label>
                <Input
                  value={descontoForm.descricao}
                  onChange={(e) => setDescontoForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder={DEDUCTION_TYPES[descontoForm.tipo] || 'Descrição do desconto'}
                  className="h-14 text-lg border-2"
                  data-testid="desconto-descricao-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Valor (R$)</Label>
                <Input
                  value={descontoForm.valor}
                  onChange={(e) => setDescontoForm(prev => ({ ...prev, valor: e.target.value }))}
                  placeholder="0,00"
                  className="h-14 text-lg border-2"
                  data-testid="desconto-valor-input"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Checkbox
                  id="desconto-is-fixo"
                  checked={descontoForm.is_fixo}
                  onCheckedChange={(checked) => setDescontoForm(prev => ({ ...prev, is_fixo: !!checked }))}
                  className="w-8 h-8 border-2"
                  data-testid="desconto-is-fixo-checkbox"
                />
                <Label htmlFor="desconto-is-fixo" className="text-lg font-semibold cursor-pointer">
                  Desconto fixo (aparece todo mês)
                </Label>
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDescontoModal(false)}
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                <X size={18} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleSaveDesconto}
                className="h-12 w-full sm:w-auto px-6 text-base bg-red-600 hover:bg-red-700"
                data-testid="save-desconto-btn"
              >
                <Save size={18} className="mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-[#1B2A4A]">
                Tem certeza que deseja excluir?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3">
              <AlertDialogCancel className="h-12 px-6 text-base border-2">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deleteConfirm?.type === 'receita') {
                    handleDeleteReceita(deleteConfirm.id);
                  } else {
                    handleDeleteDesconto(deleteConfirm.id);
                  }
                }}
                className="h-12 px-6 text-base bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

export default ReceitasPage;
